import React, { useState } from "react";
import AddAccountModal from "../model/AddAccountModal";
import Loader from "../Loader/Loader";
import { Icon } from "@iconify/react/dist/iconify.js";
import Papa from "papaparse";
import {
  copyToClipboard,
  handleError,
  trimAddress,
} from "../../utils/helper";
import { IoCopyOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { generateMerkleTree } from "../../utils/Working_generateLeafHash";
import { useWriteContract } from "wagmi";
import AirdropContractAbi from "../../Contract/AirdropContractAbi.json";
import {
  AIRDROP_CONTRACT_ADDRESS,
  AIRDROP_STATUS,
  TOKEN,
} from "../../utils/constant";
import { useAppKitAccount } from "@reown/appkit/react";
import useApi from "../../hook/useApi";
import { readContract } from "@wagmi/core";
import { config } from "../../config";
import { utils } from "ethers";
import Swal from "sweetalert2";
import { isValidEVMAddress } from "../../utils/evm.helper";
import { v4 as uuidv4 } from "uuid";

const BulkUploadLayer = () => {
  const { AsyncPostApiCall } = useApi();
  const [parsedData, setParsedData] = useState([]);
  const [originalFileData, setOriginalFileData] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [airdropList, setAirdropList] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const { isConnected, address } = useAppKitAccount();
  const [errors, setErrors] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [csvFileList, setCsvFileList] = useState([]);
  const { writeContractAsync } = useWriteContract();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const file = files[0];
    const fileId = uuidv4();
    if (!file) {
      toast.error("No file selected. Please upload a valid CSV file.");
      return;
    }

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Invalid file type. Please upload a CSV file.");
      resetFileInput();
      return;
    }

    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const csvData = result.data;
        const expectedHeaders = ["Account Address", "Amount"];

        if (
          !result.meta.fields ||
          result.meta.fields.length !== 2 ||
          result.meta.fields[0] !== expectedHeaders[0] ||
          result.meta.fields[1] !== expectedHeaders[1]
        ) {
          toast.error("Invalid CSV format.");
          setLoading(false);
          resetFileInput();
          return;
        }

        if (csvData.length === 0) {
          toast.error("The CSV file is empty. Please upload a valid CSV file.");
          setLoading(false);
          resetFileInput();
          return;
        }

        const validationErrors = [];
        const validData = [];
        const validAirdropList = [];

        csvData.forEach((row, index) => {
          const accountAddress = row["Account Address"]?.trim();
          const amount = row["Amount"]?.trim();

          if (!accountAddress || !isValidEVMAddress(accountAddress)) {
            validationErrors.push(
              `Row ${index + 1}: Invalid or missing EVM address.`
            );
            return;
          }

          const parsedAmount = parseFloat(amount);
          if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
            validationErrors.push(
              `Row ${index + 1}: Amount must be a positive number.`
            );
            return;
          }

          const uniqueId = uuidv4();
          validData.push({
            uniqueId,
            accountAddress,
            amount: parsedAmount.toString(),
            action: "Edit",
            fileId,
          });

          validAirdropList.push({
            recipient: accountAddress,
            amount: parsedAmount.toString(),
            fileId,
            uniqueId,
          });
        });

        if (validationErrors.length > 0) {
          toast.error(`CSV validation failed:\n${validationErrors.join("\n")}`);
          setLoading(false);
          resetFileInput();
          return;
        }

        setCsvFileList((prevData) => [...prevData, { file, fileId }]);
        setParsedData((prevData) => [...prevData, ...validData]);
        setOriginalFileData((prevData) => [...prevData, ...validData]);
        setAirdropList((prevData) => [...prevData, ...validAirdropList]);
        toast.success("CSV file processed successfully.");
        setLoading(false);
        resetFileInput();
      },
      error: (error) => {
        toast.error("Error parsing CSV file. Please ensure it is a valid CSV.");
        setLoading(false);
        resetFileInput();
      },
    });
  };

  const resetFileInput = () => {
    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setCsvFileList((prevData) => {
      const fileToRemove = prevData[index];
      if (!fileToRemove) return prevData;
      const fileId = fileToRemove.fileId;

      setParsedData((prev) => prev.filter((item) => item.fileId !== fileId));
      setOriginalFileData((prev) =>
        prev.filter((item) => item.fileId !== fileId)
      );
      setAirdropList((prev) => prev.filter((item) => item.fileId !== fileId));

      return prevData.filter((_, i) => i !== index);
    });
    toast.success("File removed successfully");
  };

  const handleRemoveData = (uniqueId) => {
    setParsedData((prev) => {
      const newData = prev.filter((item) => item.uniqueId !== uniqueId);
      const fileId = prev.find((item) => item.uniqueId === uniqueId)?.fileId;

      setAirdropList((prevAirdrop) =>
        prevAirdrop.filter((item) => item.uniqueId !== uniqueId)
      );

      setOriginalFileData((prevOriginal) =>
        prevOriginal.filter((item) => item.uniqueId !== uniqueId)
      );

      if (fileId) {
        const remainingEntries = newData.filter(
          (item) => item.fileId === fileId
        );
        if (remainingEntries.length === 0) {
          setCsvFileList((prevFiles) =>
            prevFiles.filter((file) => file.fileId !== fileId)
          );
        }
      }

      return newData;
    });
    toast.success("Entry removed successfully");
  };

  const handleSaveTask = async (data) => {
    try {
      setLoading(true);
      console.log("data: ", data);
      setShowModal(false);
      if (editMode) {
        const getData = (prevData) => {
          const updatedData = prevData.map((row) =>
            row.uniqueId === currentTask?.uniqueId
              ? {
                ...row,
                amount: data.amount,
                accountAddress: data.walletAddress,
              }
              : row
          );

          const airdropListData = updatedData
            .filter(
              (row) =>
                row.accountAddress &&
                row.amount &&
                row.accountAddress !== "N/A" &&
                row.amount !== "N/A"
            )
            .map((row) => ({
              recipient: row.accountAddress,
              amount: row.amount,
              fileId: row.fileId,
              uniqueId: row.uniqueId,
            }));

          setAirdropList(airdropListData);
          return updatedData;
        };
        setParsedData((prevData) => getData(prevData));
        setOriginalFileData((prevData) => getData(prevData));
        toast.success("Entry updated successfully");
      } else {
        const fileId = uuidv4();
        const uniqueId = uuidv4();
        const getData = (prevData) => [
          {
            uniqueId,
            accountAddress: data["walletAddress"] || "N/A",
            amount: data["amount"] || "N/A",
            action: "Edit",
            fileId,
          },
          ...prevData,
        ];
        setParsedData((prevData) => getData(prevData));
        setOriginalFileData((prevData) => getData(prevData));
        setAirdropList((prevData) => [
          {
            recipient: data["walletAddress"] || "N/A",
            amount: data["amount"] || "N/A",
            fileId,
            uniqueId,
          },
          ...prevData,
        ]);
        toast.success("Entry added successfully");
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
      setEditMode(false);
    }
  };

  const transactionCall = async (data, status) => {
    let payload = {
      merkleRoot: data?.merkleRoot,
      content: data?.claimsData?.map((item) => ({
        ...item,
        quantity: parseFloat(item.quantity),
      })),
    };
    if (status) {
      payload.status = status;
    }
    const result = await AsyncPostApiCall("/airdrop/create", payload);
    console.log("api result: ", result);
    if (result?.success && result?.data) {
      toast.success(result?.message || "Airdrop data created successfully");
      setAirdropList([]);
      setParsedData([]);
      setOriginalFileData([]);
      setCsvFileList([]);
      setCurrentTask(null);
      setEditMode(false);
      setShowModal(false);
      setErrors("");
    } else {
      toast.error(result?.errors?.[0]);
    }
  };

  const handleAirdropTokens = async () => {
    try {
      setLoading(true);
      if (errors) {
        toast.error(errors);
        return;
      }
      const result = await generateMerkleTree(airdropList);

      const tupleList = airdropList.map((item) => [
        item.recipient,
        utils.parseUnits(item.amount.toString(), 18).toString(),
      ]);

      const data = {
        abi: AirdropContractAbi,
        address: AIRDROP_CONTRACT_ADDRESS,
        functionName: "airdropERC20",
        args: [TOKEN, tupleList],
      };

      await readContract(config, { ...data, account: address });

      const tx = await writeContractAsync(data);

      if (tx) {
        await transactionCall(result, AIRDROP_STATUS.SUCCESS);

        // 🔔 Send notification to each user
        for (const user of airdropList) {
          await sendNotification(
            user.recipient,
            "Airdrop Tokens Received",
            `You have successfully received ${user?.amount} tokens.`
          );
        }
      } else {
        toast.error("Contract call failed");
      }
    } catch (error) {
      const contractError = handleError(error);
      toast.error(contractError);
    } finally {
      setLoading(false);
    }
  };

  const handleAirdropWhitelist = async () => {
    try {
      setLoading(true);
      if (errors) {
        toast.error(errors);
        return;
      }
      const result = await generateMerkleTree(airdropList);
      if (!result) {
        toast.error("Merkle root is not generated");
        return;
      }

      const data = {
        abi: AirdropContractAbi,
        address: AIRDROP_CONTRACT_ADDRESS,
        functionName: "setMerkleRoot",
        args: [TOKEN, result?.merkleRoot, true],
      };

      await readContract(config, { ...data, account: address });

      const tx = await writeContractAsync(data);
      if (tx) {
        await transactionCall(result);

        for (const user of airdropList) {
          await sendNotification(
            user.recipient,
            "Airdrop Whitelist Accepted",
            "You have been added to the airdrop whitelist successfully."
          );
        }
        console.log("tx: ", tx);
      } else {
        toast.error("Contract call failed");
      }
    } catch (error) {
      console.log("error: ", error);
      const contractError = handleError(error);
      toast.error(contractError);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const csvData = [
      ["Account Address", "Amount"],
      ["0x1234567890abcdef1234567890abcdef12345678", "100.50"],
      ["0xabcdef1234567890abcdef1234567890abcdef12", "250.75"],
      ["0x7890abcdef1234567890abcdef1234567890abcd", "75.25"],
      ["0x7890abcdef1234567890abcdef1234567890abcd", "75.25"],
      ["0x7890abcdef1234567890abcdef1234567890abcd", "75.25"],
      ["0x7890abcdef1234567890abcdef1234567890abcd", "75.25"],
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    const validChars = /^[a-zA-Z0-9\s]*$/;

    if (!value.trim()) {
      setParsedData(originalFileData);
      setSearchValue("");
    } else if (validChars.test(value)) {
      setParsedData(
        originalFileData.filter((row) =>
          row.accountAddress.toLowerCase().includes(value.toLowerCase())
        )
      );
      setSearchValue(value);
    }
  };

  const clearAllData = () => {
    setParsedData([]);
    setAirdropList([]);
    setOriginalFileData([]);
    setCsvFileList([]);
    toast.success("All data cleared successfully");
  };

  const sendNotification = async (
    walletAddress,
    title,
    description,
    image = ""
  ) => {
    try {
      const payload = {
        userWalletAddress: walletAddress,
        title,
        description,
        image,
      };

      const res = await AsyncPostApiCall("/notifications/send", payload);
      if (res?.success) {
        console.log(
          `Notification sent to ${trimAddress(walletAddress, 6, 6)}`
        );
      } else {
        toast.error("Failed to send notification");
      }
    } catch (error) {
      console.error("Notification error:", error);
      toast.error("Error sending notification");
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <h6 className="fw-semibold mb-0">File Upload</h6>
        </div>
        <div className="card-body p-24 d-flex">
          <div
            className="table-container"
            style={{ flex: 1, marginRight: "10px" }}
          >
            <div
              className={`drop-area ${dragActive ? "drag-active" : ""}`}
              style={{
                border: "2px dashed rgb(75, 95, 115)",
                borderRadius: "8px",
                padding: "40px",
                textAlign: "center",
                background: "#fff",
                marginBottom: "30px",
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <p style={{ color: "#6c757d" }}>
                Drag and drop or browse your file to get started
              </p>
              <input
                type="file"
                id="fileInput"
                accept=".csv"
                onChange={handleChange}
                style={{ display: "none" }}
              />
              <button
                onClick={() => document.getElementById("fileInput").click()}
                style={{
                  background: "#007bff",
                  color: "#fff",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Browse your files
              </button>
              <br />
              {csvFileList.length > 0 ? (
                <div className="mt-20 w-100 d-flex justify-content-start flex-wrap gap-10">
                  {csvFileList.map((file, index) => (
                    <div className="file-card" key={index}>
                      <div className="file-icon">
                        <Icon
                          icon="lucide:file"
                          className="file-icon-img"
                          aria-hidden="true"
                        />
                      </div>
                      <span className="file-name">
                        {file?.file?.name || "Unknown File"}
                      </span>
                      <button
                        type="button"
                        className="remove-button bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-20-px h-20-px d-flex justify-content-center align-items-center rounded-circle"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to remove this file?`
                            )
                          ) {
                            handleRemoveFile(index);
                          }
                        }}
                        aria-label={`Remove ${file?.name || "file"}`}
                        title="Remove file"
                      >
                        <Icon
                          icon="clarity:close-line"
                          className="menu-icon"
                          aria-hidden="true"
                        />
                        <span className="visually-hidden">Remove file</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-danger mt-3">
                  * Only CSV file is allowed
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-10 justify-content-between mb-10 border-bottom pb-10">
              <h6 className="fw-semibold mb-0">File Details</h6>
              <div className="d-flex align-items-center gap-10">
                <div
                  className="d-flex align-items-center gap-10 btn btn-primary"
                  onClick={handleDownload}
                >
                  <button type="button">Sample File</button>
                  <Icon icon="ic:baseline-download" className="icon text-xl" />
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    console.log("clicked");
                    setShowModal(true);
                    setCurrentTask(null);
                  }}
                >
                  + Add Account
                </button>
              </div>
            </div>
            <div className="mb-20">
              <div className="d-flex align-items-center gap-10 justify-content-between">
                <input
                  type="text"
                  placeholder="Search"
                  style={{
                    width: "30%",
                    padding: "8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                  }}
                  value={searchValue}
                  onChange={handleSearch}
                // disabled={parsedData.length === 0}
                />
                <div className="d-flex align-items-center gap-10">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to clear all data?"
                        )
                      ) {
                        clearAllData();
                      }
                    }}
                    disabled={parsedData.length === 0}
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      const result = await Swal.fire({
                        text: "Do you want to send tokens to the users or accept the whitelist?",
                        showCancelButton: true,
                        confirmButtonText: "Send Tokens",
                        cancelButtonText: "Accept Whitelist",
                        confirmButtonColor: "#3085d6",
                        cancelButtonColor: "#d33",
                      });
                      if (result?.isConfirmed) {
                        handleAirdropTokens();
                      } else if (
                        result?.isDismissed &&
                        result?.dismiss === "cancel"
                      ) {
                        handleAirdropWhitelist();
                      }
                    }}
                    disabled={!isConnected || airdropList.length <= 1}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      style={{ textAlign: "left", padding: "10px" }}
                    >
                      S No.
                    </th>
                    <th
                      scope="col"
                      style={{ textAlign: "left", padding: "10px" }}
                    >
                      Account Address
                    </th>
                    <th
                      scope="col"
                      style={{ textAlign: "left", padding: "10px" }}
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      style={{ textAlign: "left", padding: "10px" }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        style={{ textAlign: "center", padding: "10px" }}
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    parsedData.map((row, index) => (
                      <tr
                        key={row.uniqueId}
                        style={{ borderBottom: "1px solid #dee2e6" }}
                      >
                        <td style={{ padding: "10px" }}>{index + 1}</td>
                        <td style={{ padding: "10px" }}>
                          <span>
                            {trimAddress(row?.accountAddress, 6, 6)}
                            {row?.accountAddress && (
                              <button
                                onClick={() =>
                                  copyToClipboard(row?.accountAddress)
                                }
                                className="ms-3 p-0 border-0 bg-transparent"
                                style={{ lineHeight: 1 }}
                              >
                                <IoCopyOutline />
                              </button>
                            )}
                          </span>
                        </td>
                        <td style={{ padding: "10px" }}>{row.amount}</td>
                        <td style={{ padding: "10px" }}>
                          <div className="d-flex align-items-center gap-10">
                            <button
                              type="button"
                              className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => {
                                setShowModal(true);
                                setCurrentTask(row);
                                setEditMode(true);
                              }}
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                              <span className="tooltip-text">Edit</span>
                            </button>
                            <button
                              type="button"
                              className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => {
                                handleRemoveData(row.uniqueId);
                              }}
                              aria-label={`Remove entry for ${trimAddress(
                                row?.accountAddress,
                                6,
                                6
                              )}`}
                            >
                              <Icon icon="lucide:trash" className="menu-icon" />
                              <span className="tooltip-text">Remove</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <AddAccountModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          handleSave={handleSaveTask}
          task={currentTask}
          loading={loading}
          editMode={editMode}
        />
      )}
    </>
  );
};

export default BulkUploadLayer;
